import { GoogleGenAI, Type, FunctionDeclaration, Modality, LiveServerMessage } from "@google/genai";
import { AIIntelligenceConfig, ProviderConfig, ModelConfig } from "../types";
import { authHeaders } from '../utils/auth';

export class AIService {
  public config: AIIntelligenceConfig;
  private lastRequestTime: number = 0;
  private minInterval: number = 1000; // Reduced for better responsiveness
  private isCoolingDown: boolean = false;
  private cooldownEnd: number = 0;
  private responseCache: Map<string, { data: any, timestamp: number }> = new Map();
  private cacheTimeout: number = 5 * 60 * 1000; // 5 minutes cache timeout
  private performanceMetrics: Map<string, { count: number, avgTime: number, lastTime: number }> = new Map();
  private configListeners: Array<() => void> = [];

  // New feature: Neural Chat Endpoint configuration
  private neuralChatEndpointId: string | null = null; // null represents default OmniGate local LLM

  constructor(config: AIIntelligenceConfig) {
    this.config = config;
  }

  // --- Configuration Methods ---

  public setNeuralChatEndpoint(endpointId: string | null) {
    this.neuralChatEndpointId = endpointId;
    console.log(`Neural Chat Endpoint set to: ${endpointId || 'OmniGate Local'}`);
    this.notifyConfigChange();
  }

  public getNeuralChatEndpoint(): string | null {
    return this.neuralChatEndpointId;
  }

  public onConfigChange(handler: () => void) {
    this.configListeners.push(handler);
    return () => {
      this.configListeners = this.configListeners.filter(h => h !== handler);
    };
  }

  private notifyConfigChange() {
    this.configListeners.forEach(h => h());
  }

  public getActiveConfigModel() {
    const { agent } = this.config;
    return {
      id: this.neuralChatEndpointId || agent.modelId,
      name: this.neuralChatEndpointId || agent.modelId,
      provider: 'Kernel Config',
      status: 'ONLINE'
    };
  }

  public async testActiveModels(): Promise<any> {
    try {
      const response = await fetch('/v1/models/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      if (!response.ok) throw new Error(`Model test failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error("Failed to test active models:", error);
      throw error;
    }
  }

  public async getAvailableModels(): Promise<any[]> {
    try {
      const response = await fetch('/v1/models', { headers: authHeaders() });
      if (!response.ok) return [];
      const data = await response.json();
      return data.data || data.models || [];
    } catch (error) {
      console.error("Failed to fetch available models:", error);
      return [];
    }
  }

  // --- End Configuration Methods ---

  updateConfig(newConfig: AIIntelligenceConfig) {
    this.config = newConfig;
    this.notifyConfigChange();
  }

  private isGeminiAvailable(): boolean {
    return false;
  }

  private async throttle() {
    const now = Date.now();
    if (this.isCoolingDown) {
      if (now < this.cooldownEnd) {
        await new Promise(r => setTimeout(r, 500)); // Reduced from 1000ms
      } else {
        this.isCoolingDown = false;
      }
    }
    const elapsed = now - this.lastRequestTime;
    const requiredInterval = this.isCoolingDown ? 3000 : this.minInterval; // Reduced from 5000ms
    if (elapsed < requiredInterval) {
      await new Promise(r => setTimeout(r, requiredInterval - elapsed));
    }
    this.lastRequestTime = Date.now();
  }

  private generateCacheKey(url: string, body: any): string {
    const text = body.input || body.messages?.[body.messages.length - 1]?.content || '';
    // Safe btoa for Unicode characters
    const jsonString = JSON.stringify({ text: text.slice(0, 100) });
    const base64String = btoa(unescape(encodeURIComponent(jsonString)));
    return `${url}_${base64String}`;
  }

  private getCachedResponse(cacheKey: string) {
    const cached = this.responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    // Remove expired cache
    if (cached) {
      this.responseCache.delete(cacheKey);
    }
    return null;
  }

  private setCachedResponse(cacheKey: string, data: any) {
    this.responseCache.set(cacheKey, { data, timestamp: Date.now() });
    // Clean up old entries periodically
    if (this.responseCache.size > 100) { // Limit cache size
      const firstKey = this.responseCache.keys().next().value;
      this.responseCache.delete(firstKey);
    }
  }

  // Performance monitoring
  private recordPerformance(operation: string, startTime: number) {
    const duration = Date.now() - startTime;

    if (!this.performanceMetrics.has(operation)) {
      this.performanceMetrics.set(operation, { count: 0, avgTime: 0, lastTime: 0 });
    }

    const metrics = this.performanceMetrics.get(operation)!;
    metrics.count += 1;
    metrics.avgTime = ((metrics.avgTime * (metrics.count - 1)) + duration) / metrics.count;
    metrics.lastTime = duration;

    // Log slow operations
    if (duration > 5000) { // 5 seconds
      console.warn(`Slow operation detected: ${operation} took ${duration}ms`);
    }
  }

  public getPerformanceMetrics() {
    return Array.from(this.performanceMetrics.entries()).map(([operation, metrics]) => ({
      operation,
      ...metrics,
      cacheHitRate: this.getCacheHitRate(operation)
    }));
  }

  private getCacheHitRate(operation: string): number {
    // This would require tracking cache hits vs misses per operation
    // For now, return a placeholder
    return 0.85; // 85% assumed cache hit rate
  }

  public clearCache() {
    this.responseCache.clear();
    console.log('AI Service cache cleared');
  }

  public getCacheStats() {
    return {
      size: this.responseCache.size,
      maxSize: 100,
      utilization: Math.round((this.responseCache.size / 100) * 100)
    };
  }

  private async safeJsonParse(response: Response) {
    const text = await response.text();

    // Handle empty responses
    if (!text || text.trim() === '') {
      return { response: '', error: 'Empty response received' };
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      const lines = text.trim().split('\n');
      if (lines.length > 1) {
        try {
          return JSON.parse(lines[lines.length - 1]);
        } catch (innerE) {
          let mergedResponse = "";
          for (const line of lines) {
            try {
              const parsed = JSON.parse(line);
              mergedResponse += (parsed.response || parsed.message?.content || "");
              if (parsed.done) return { response: mergedResponse, done: true };
            } catch { continue; }
          }
          if (mergedResponse) return { response: mergedResponse };
        }
      }

      // More robust error handling - try to extract meaningful content
      const cleanedText = text.trim();
      if (cleanedText.startsWith('{') && cleanedText.endsWith('}')) {
        // Try to fix common JSON issues
        let fixedText = cleanedText
          .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
          .replace(/([\[{])(\s*,)/g, '$1') // Remove leading commas
          .replace(/([^\\])"([^"\\]*(?:\\.[^"\\]*)*)"/g, '$1"$2"'); // Fix quotes

        try {
          return JSON.parse(fixedText);
        } catch (fixError) {
          // If still fails, return as text response
          return { response: cleanedText, error: 'Malformed JSON response' };
        }
      }

      // Return as plain text if not JSON
      return { response: cleanedText, error: 'Non-JSON response' };
    }
  }

  private isRateLimitError(err: any): boolean {
    if (!err) return false;
    if (err.error?.code === 429 || err.status === 429 || err.code === 429) return true;
    const msg = typeof err === 'string' ? err : (err.message || '');
    return msg.includes('429') || msg.includes('RESOURCE_EXHAUSTED') || msg.includes('quota');
  }

  // A2-4 FIX: Dodano parametr fastMode dla wydajności Vision Observer
  // Vision model fallback chain — jeśli primary niedostępny, próbuj kolejne
  // 1. qwen2.5vl:3b  — local Ollama, primary (fits 12GB VRAM)
  // 2. meta/llama-3.2-11b-vision-instruct — NVIDIA NIM, lepsza precyzja
  // 3. nvidia/llama-3.1-nemotron-nano-vl-8b-v1 — NVIDIA NIM, szybki fallback
  private static VISION_FALLBACK_CHAIN = [
    'microsoft/phi-4-multimodal-instruct',    // NIM — 2s, domyślny
    'meta/llama-3.2-11b-vision-instruct',     // NIM — 5.5s, lepsza precyzja
    'nvidia/llama-3.1-nemotron-nano-vl-8b-v1', // NIM — 5.7s
    'meta/llama-4-scout-17b-16e-instruct',    // NIM cloud — fallback
  ];

  async analyzeVision(videoData: string | string[], mimeType: string, prompt: string, fastMode: boolean = false, overrideModel?: string): Promise<string> {
    const images = Array.isArray(videoData) ? videoData : [videoData];

    // If caller specified a model, use it first; otherwise use fallback chain
    const chain = overrideModel
      ? [overrideModel, ...AIService.VISION_FALLBACK_CHAIN.filter(m => m !== overrideModel)]
      : AIService.VISION_FALLBACK_CHAIN;

    // Try each model in chain
    for (const visionModel of chain) {
      try {
        const response = await fetch('/v1/vision/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            image_base64: images[0],
            prompt,
            model: visionModel,
            fast_mode: fastMode,
            response_max_tokens: fastMode ? 2048 : 4096  // zwiększone: NIM obsługuje do 8k, Ollama do 4800
          })
        });
        const data = await this.safeJsonParse(response);
        if (data.analysis && data.analysis.trim()) return data.analysis.trim();
        // Auth error or empty result — log and try next model instead of falling to OpenAI-compat
        if (data.detail || data.error) {
          console.warn(`[Vision] ${visionModel}: ${data.detail || data.error}`);
        }
        // Do NOT fall to localVisionRequest (OpenAI-compat /v1/chat/completions)
        // — it doesn't properly handle image_url for Ollama VL models.
        continue;
      } catch (e: any) {
        console.warn(`[Vision] Model ${visionModel} failed: ${e.message}, trying next...`);
        continue;
      }
    }
    // All models failed — return empty string
    // AgentChat.tsx handles empty VLM result by sending metadata (no base64) to main model
    console.warn('[Vision] All VLM models failed — returning empty string');
    return '';
  }

  private async localVisionRequest(videoData: string | string[], mimeType: string, prompt: string, config: ProviderConfig): Promise<string> {
    const images = Array.isArray(videoData) ? videoData : [videoData];
    try {
      const content: any[] = [{ type: "text", text: prompt }];
      images.forEach(img => content.push({ type: "image_url", image_url: { url: `data:${mimeType};base64,${img}` } }));
      const response = await fetch('/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
          'X-Target-URL': config.baseUrl || ''
        },
        body: JSON.stringify({
          model: config.modelId,
          messages: [{ role: "user", content }],
          stream: false,
          keep_alive: 0,
          max_tokens: 1500  // per-segment: opis klatek, nie potrzeba więcej
        })
      });
      const data = await this.safeJsonParse(response);
      return data.choices?.[0]?.message?.content || data.response || "Vision Node Error.";
    } catch (e: any) { return `Vision Node Error: ${e.message}`; }
  }

  async runAgentSession(prompt: string, history: any[] = [], mcpEnabled: boolean = true, signal?: AbortSignal, allowedServers?: string[], numCtx?: number, maxOutputTokens?: number) {
    const { agent } = this.config;
    
    // Debug: Log API key status before making request
    if (typeof window !== 'undefined') {
      const apiKey = localStorage.getItem('omnigate_api_key');
      const isMobile = !!(window as any).Capacitor?.isNativePlatform?.();
      console.log(`[runAgentSession] API Key present: ${!!apiKey}, Is Mobile: ${isMobile}, Model: ${this.neuralChatEndpointId || agent.modelId}`);
      if (!apiKey) {
        console.error('[runAgentSession] CRITICAL: No API key! Request will fail with 401.');
      }
    }
    
    try {
      const messages = history.map(h => {
        const msg: any = {
          role: h.role === 'model' ? 'assistant' : h.role,
          content: h.parts ? (h.parts[0]?.text || "") : (h.content || "")
        };
        if (h.toolCalls && h.toolCalls.length > 0) {
          msg.tool_calls = h.toolCalls;
        }
        if (h.toolCallId) {
          msg.tool_call_id = h.toolCallId;
          msg.name = h.name;
        }
        return msg;
      });
      if (prompt) {
        messages.push({ role: 'user', content: prompt });
      }

      const requestModelId = this.neuralChatEndpointId || agent.modelId;

      // Generate cache key based on prompt and recent history
      const cacheKey = this.generateCacheKey('/v1/chat/completions', {
        messages: messages.slice(-2), // Last 2 messages for context
        model: requestModelId
      });
      const cachedResponse = this.getCachedResponse(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      let tools = mcpEnabled ? await this.getMCPTools() : [];
      if (mcpEnabled && allowedServers && allowedServers.length > 0) {
        tools = tools.filter(t => allowedServers.includes(t.server));
      }
      // BUG-03 FIX: Normalizuj schematy MCP do pełnego OpenAI JSON Schema format.
      // Ollama odrzuca niekompletne schematy (brak type:object, brak properties).
      const openAiTools = tools
        .filter(t => t.name && t.description)
        .map(t => {
          const raw = t.inputSchema || t.parameters || {};
          const schema = {
            type: 'object',
            properties: (raw as any).properties || {},
            required: (raw as any).required || []
          };
          return {
            type: 'function' as const,
            function: {
              name: t.name,
              description: t.description || t.name,
              parameters: schema
            }
          };
        });
      // Qwen3 think-block hardening: disable thinking for pure text tasks (synthesis)
      // When mcpEnabled=false, we're doing synthesis/text-only — suppress <think> mode
      // Enable streaming for all requests to avoid timeouts on slow cloud models
      const body: any = { model: requestModelId, messages, stream: true, ...(!mcpEnabled ? { think: false, max_tokens: maxOutputTokens ?? 6144 } : { max_tokens: maxOutputTokens ?? 4096 }), num_ctx: numCtx ?? 8192 };
      if (openAiTools.length > 0) {
        // Detect if model is a local Ollama model (no "/" separator, or has ":" tag)
        // vs. a cloud/API model routed through proxy (contains "/" like "deepseek-ai/deepseek-v3")
        // VLMs (qwen3-vl, qwen2.5vl, minicpm) also need ReAct-only — they crash with native tools.
        const isLocalOllamaModel = !requestModelId.includes('/') ||
          /qwen.*vl|minicpm|llava|bakllava|moondream|cogvlm|fuyu/i.test(requestModelId);

        if (!isLocalOllamaModel) {
          // Cloud/API model: send native tools in body — model handles tool_calls natively
          body.tools = openAiTools;
          body.tool_choice = 'auto';
        }

        // Always inject ReAct prompt as fallback — used when native tool_calls come back empty
        // (some API models, Ollama text models, or when model ignores native tools)
        const toolDescriptions = openAiTools.map(t =>
          `- ${t.function.name}: ${t.function.description}\n  Arguments JSON schema: ${JSON.stringify(t.function.parameters)}`
        ).join("\n\n");

          const reactPrompt = `### INSTRUKCJA RE-ACT DLA OMNIGATE ###
Dostępne narzędzia MCP:
${toolDescriptions}

ZASADY DZIAŁANIA (ŁAŃCUCH MYŚLOWY):
    1. NIE ujawniaj wewnętrznego toku rozumowania. Odpowiadaj zwięźle i zadaniowo.
    2. NIGDY NIE WYMYŚLAJ NARZĘDZI ANI ICH PARAMETRÓW. Używaj WYŁĄCZNIE nazw z listy "Dostępne narzędzia MCP".
    3. Dobór narzędzia wg intencji:
      - zapytania o system lokalny (porty/procesy/pliki/shell) => wybieraj narzędzia systemowe (np. desktop-commander), NIE run_browser_agent.
      - zapytania o web/internet (np. pogoda, wyszukiwanie strony, nawigacja) => run_browser_agent z argumentem {"task": "..."}.
      - wieloetapowe research/synteza źródeł => run_deep_research z argumentem {"task": "..."}.
    4. Dla 'run_browser_agent' lub 'run_deep_research' ZAWSZE przekazuj 'task' jako opis akcji. Nie wysyłaj parametru 'url'.
    5. PRACA ZE SKILLAMI (1100+ umiejętności w 'skills-manager'):
   - Jeśli zadanie wymaga specjalistycznej wiedzy (np. video, 3D, specyficzne API), użyj narzędzia 'find_skill_by_trigger' ("skills-manager"), aby znaleźć odpowiedni skill.
   - Następnie ZAWSZE użyj 'get_skill_info' ("skills-manager"), aby dowiedzieć się co dany skill robi i jakie ma parametry.
   - Na koniec wykonaj zadanie używając 'execute_skill' z odpowiednimi argumentami.
    6. Jeśli potrzebujesz narzędzia, zwróć TYLKO tag <tool_call> w następującym formacie:
       <tool_call>{"name": "dokładna_nazwa_narzędzia", "arguments": {"klucz": "wartosc"}}</tool_call>
       NIE używaj bloków \`\`\`json {...}\`\`\` zamiast <tool_call> — parser ich NIE czyta jako wywołanie narzędzia.
       NIE pisz JSON poza tagiem <tool_call> — to zostanie potraktowane jako tekst, nie akcja.
       NIE opisuj słowami co zamierzasz zrobić — po prostu WYWOŁAJ narzędzie tagiem <tool_call>.
       Po wygenerowaniu <tool_call> NATYCHMIAST zatrzymaj się — system sam wywoła narzędzie i dostarczy wynik.
    7. Po wywołaniu narzędzia system odpowie formą [Wynik narzędzia ...].
    8. ⚡ KLUCZOWA ZASADA KONTYNUACJI: Po otrzymaniu [Wynik narzędzia ...] NATYCHMIAST wywołaj kolejne narzędzie przez <tool_call> jeśli zadanie nie jest skończone.
       ZAKAZ pisania "Planuję...", "Następnie sprawdzę...", "Zrobię X..." — ZAMIAST TEGO od razu wstaw <tool_call>.
       Dopiero gdy WSZYSTKIE kroki są wykonane i zadanie jest w 100% ukończone, wtedy udziel finalnej odpowiedzi po polsku.
       PRZYKŁAD POPRAWNEGO ZACHOWANIA po wyniku narzędzia:
         ✅ [Wynik narzędzia foo]: {...}  →  <tool_call>{"name": "bar", "arguments": {...}}</tool_call>
         ❌ [Wynik narzędzia foo]: {...}  →  "Teraz sprawdzę API na porcie 7860..."  ← TO ZATRZYMUJE AGENTA
    9. Jeśli narzędzie zwróci błąd, od razu wywołaj fallback przez <tool_call> — nie opisuj co zamierzasz zrobić.`;

        const sysMsgIndex = messages.findIndex(m => m.role === 'system');
        if (sysMsgIndex >= 0) {
          messages[sysMsgIndex].content += `\n\n[TOOL USAGE INSTRUCTIONS]\n${reactPrompt}`;
        } else {
          messages.unshift({ role: 'system', content: reactPrompt });
        }
      }
      const response = await fetch('/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({ ...body, keep_alive: 0 }),
        signal: signal
      });

      // Parse SSE streaming response
      let text = '';
      let reasoning = '';
      // Map by index to properly merge streamed tool_call fragments
      // OpenAI streaming sends function.arguments in pieces across multiple chunks
      const streamedToolCallsMap: Map<number, any> = new Map();

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const payload = line.replace(/^data:\s*/, '').trim();
            if (payload === '[DONE]') break;
            try {
              const chunk = JSON.parse(payload);
              const delta = chunk.choices?.[0]?.delta;
              if (delta?.content) text += delta.content;
              if (delta?.reasoning_content) reasoning += delta.reasoning_content;
              // Merge tool_call fragments by index
              if (delta?.tool_calls) {
                for (const tc of delta.tool_calls) {
                  const idx = tc.index ?? 0;
                  if (!streamedToolCallsMap.has(idx)) {
                    streamedToolCallsMap.set(idx, {
                      id: tc.id || `call_stream_${Date.now()}_${idx}`,
                      type: tc.type || 'function',
                      function: { name: tc.function?.name || '', arguments: '' }
                    });
                  }
                  const existing = streamedToolCallsMap.get(idx);
                  if (tc.id) existing.id = tc.id;
                  if (tc.function?.name) existing.function.name = tc.function.name;
                  if (tc.function?.arguments) existing.function.arguments += tc.function.arguments;
                }
              }
            } catch { /* skip malformed */ }
          }
        }
      } else {
        // Fallback for non-streaming responses
        const data = await this.safeJsonParse(response);
        const message = data.choices?.[0]?.message || {};
        text = message.content || data.response || '';
        reasoning = message.reasoning_content || message.reasoning || '';
        const nativeCalls = message.tool_calls || [];
        nativeCalls.forEach((tc: any, i: number) => streamedToolCallsMap.set(i, tc));
      }
      const streamedToolCalls = Array.from(streamedToolCallsMap.values());

      if (!text && reasoning) {
        text = `<think>\n${reasoning}\n</think>`;
      } else if (reasoning) {
        text = `<think>\n${reasoning}\n</think>\n${text}`;
      }
      
      if (!text && streamedToolCalls.length === 0) {
        text = "Agent Error.";
      }

      // Strip <think>...</think> blocks from the displayed text.
      // rawText (below) preserves the original content for logging/debugging.
      // This handles Qwen3 and other reasoning models that embed <think> in message.content.
      const rawTextBeforeStrip = text;
      text = text.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
      // If stripping left us with nothing but the model DID reason, show a fallback
      if (!text && rawTextBeforeStrip.includes('<think>')) {
        text = '[Model zakończył rozumowanie, ale nie wygenerował odpowiedzi tekstowej. Spróbuj ponownie lub wybierz inny model.]';
      }

      // V4: Enhanced Parser ReAct (Robust Regex + Multi-Call + Markdown handling)
      let parsedToolCalls: any[] = [];
      // Global search to handle multiple calls in one turn
      // Also handles unclosed tags: <tool_call>{...} (qwen2.5vl style)
      const toolCallRegex = /<tool_call>([\s\S]*?)<\/tool_call>/gi;
      let match;

      const processToolJSON = (jsonStr: string) => {
        try {
          let innerContent = jsonStr.trim();
          innerContent = innerContent.replace(/```json/g, '').replace(/```/g, '').trim();

          // Format: <function=name>\nargs_json_or_empty\n</function>
          // Used by some models (Hermes-style, freehuntx forks, some qwen variants)
          const funcTagMatch = innerContent.match(/^<function=([\w.-]+)>([\s\S]*?)<\/function>$/i);
          if (funcTagMatch) {
            const toolName = funcTagMatch[1].trim();
            let toolArgs: any = {};
            const argsStr = funcTagMatch[2].trim();
            if (argsStr) {
              try { toolArgs = JSON.parse(argsStr); } catch { toolArgs = { _raw: argsStr }; }
            }
            const callId = `call_react_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
            parsedToolCalls.push({
              id: callId,
              functionCall: { name: toolName, args: toolArgs },
              raw: { id: callId, type: 'function', function: { name: toolName, arguments: JSON.stringify(toolArgs) } }
            });
            return true;
          }

          const parsed = JSON.parse(innerContent);
          const toolName = parsed.name || parsed.function || parsed.tool;
          const toolArgs = parsed.arguments || parsed.args || {};

          if (toolName && toolName !== "tool_name" && toolName !== "function_name") {
            const callId = `call_react_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
            parsedToolCalls.push({
              id: callId,
              functionCall: { name: toolName, args: toolArgs },
              raw: {
                id: callId,
                type: "function",
                function: {
                  name: toolName,
                  arguments: typeof toolArgs === 'string' ? toolArgs : JSON.stringify(toolArgs)
                }
              }
            });
            return true;
          }
        } catch (e) {
          // not a valid JSON or tool call
        }
        return false;
      };

      while ((match = toolCallRegex.exec(text)) !== null) {
        processToolJSON(match[1]);
      }

      // Fallback: unclosed <tool_call> tags (qwen2.5vl outputs without </tool_call>)
      if (parsedToolCalls.length === 0 && text.includes('<tool_call>')) {
        const unclosedRegex = /<tool_call>([\s\S]+?)(?:<\/tool_call>|$)/gi;
        let um;
        while ((um = unclosedRegex.exec(text)) !== null) {
          processToolJSON(um[1]);
        }
      }

      // Fallback 1: raw JSON object at start of text
      if (parsedToolCalls.length === 0 && text.trim().startsWith('{') && text.trim().endsWith('}')) {
        if (processToolJSON(text)) {
          text = "";
        }
      }

      // Fallback 2: ```json {...}``` blocks outside <tool_call> tags
      // Qwen-coder sometimes outputs ```json\n{"name":...}\n``` instead of <tool_call>
      if (parsedToolCalls.length === 0) {
        const jsonBlockRegex = /```json\s*([\s\S]*?)```/gi;
        let jMatch;
        while ((jMatch = jsonBlockRegex.exec(text)) !== null) {
          const candidate = jMatch[1].trim();
          if ((candidate.includes('"name"') || candidate.includes("'name'")) &&
              (candidate.includes('"arguments"') || candidate.includes('"args"'))) {
            if (processToolJSON(candidate)) {
              text = text.replace(jMatch[0], '').trim();
            }
          }
        }
      }

      // Remove all tool call tags from the visible text
      if (parsedToolCalls.length > 0) {
        text = text.replace(/<tool_call>[\s\S]*?<\/tool_call>/gi, "").trim();
        // Also strip unclosed <tool_call> tags (qwen2.5vl style)
        text = text.replace(/<tool_call>[\s\S]*/gi, "").trim();
        if (!text || text === "```json\n\n```" || text === "```json\n```") {
          text = "Procesowanie zapytań systemowych...";
        }
      }

      // Połączenie narzędzi z Ollama API (zależnie czy model zwrócił natywnie czy przez ReAct)
      // streamedToolCalls already contains tool_calls from both streaming and non-streaming paths
      const nativeToolCalls = (streamedToolCalls.map((tc: any, index: number) => {
        try {
          const callId = tc.id || `call_${Date.now()}_${index}`;
          tc.id = callId; // ensure it has an id
          return {
            id: callId,
            functionCall: {
              name: tc.function.name,
              args: typeof tc.function.arguments === 'string'
                ? JSON.parse(tc.function.arguments)
                : (tc.function.arguments || {})
            },
            raw: tc
          };
        } catch {
          return null;
        }
      }).filter(Boolean) || []) as { id: string, functionCall: { name: string; args: any }, raw: any }[];

      const toolCalls = [...nativeToolCalls, ...parsedToolCalls];
      const result = { text, toolCalls, rawText: text };
      // ANTY-PĘTLA FIX: NIE cache'uj gdy wynik zawiera tool_calls.
      // Cache powoduje że kolejne wywołanie sendToAgent dostaje IDENTYCZNE tool calls
      // z cache zamiast świeżej odpowiedzi modelu → nieskończona pętla 100x wywołań.
      if (toolCalls.length === 0) {
        this.setCachedResponse(cacheKey, result);
      }
      return result;
    } catch (e: any) {
      const errorResult = { text: `Agent Error: ${e.message}`, toolCalls: [], rawText: "" };
      // Even errors are cached temporarily to avoid repeated failures
      const cacheKey = this.generateCacheKey('/v1/chat/completions', {
        messages: [{ role: 'user', content: prompt }],
        model: agent.modelId
      });
      this.setCachedResponse(cacheKey, errorResult);
      return errorResult;
    }
  }

  async getMCPTools(): Promise<any[]> {
    const startTime = Date.now();
    try {
      const response = await fetch('/v1/mcp/tools', { headers: authHeaders() });
      if (!response.ok) {
        this.recordPerformance('getMCPTools', startTime);
        return [];
      }
      const data = await response.json();
      this.recordPerformance('getMCPTools', startTime);
      return data.tools || [];
    } catch (error) {
      this.recordPerformance('getMCPTools', startTime);
      return [];
    }
  }

  async executeMCPTool(name: string, args: any, userId: string = 'dashboard-user'): Promise<any> {
    if (name === "tool_name" || name === "function_name") {
      throw new Error("Literal placeholder used by model. Please try again with actual tool name.");
    }
    const startTime = Date.now();
    const tools = await this.getMCPTools();
    const toolDef = tools.find(t => t.name === name);
    // V4 fix: broad string match fallback if tool definition is missing locally.
    let server = 'omnigate';
    if (!toolDef?.server) {
      if (/playwright/i.test(name)) {
        server = 'playwright';
      } else if (/skill|agent|orchestrator|tts|asr|vision|infsh|file|directory|shell/.test(name)) {
        server = 'skills-manager';
      }
    } else {
      server = toolDef.server;
    }

    try {
        // Validate arguments before sending
        const validatedArgs = this.validateToolArguments(args);
        
        // V4 FIX: Ensure 'run_browser_agent' always receives a 'task' argument (only for this tool)
        if (name === "run_browser_agent" && (!validatedArgs.task || typeof validatedArgs.task !== "string")) {
            // If task is missing, empty, or not a string, construct a meaningful task description
            validatedArgs.task = typeof args === "string" ? args : (
                args?.task || args?.url || args?.command || args?.text ||
                "Przejdź na stronę internetową i wykonaj zadanie użytkownika"
            );
        }

      const response = await fetch('/v1/mcp/execute', {
        signal: AbortSignal.timeout(120000),
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          server,
          tool: name,
          arguments: validatedArgs,
          user_id: userId
        })
      });

      if (!response.ok) {
        this.recordPerformance(`executeMCPTool_${name}`, startTime);
        const errorData = await this.safeJsonParse(response);
        throw new Error(`HTTP ${response.status}: ${errorData.detail || response.statusText}`);
      }

      const result = await this.safeJsonParse(response);

      // Handle different response formats
      if (result.error) {
        this.recordPerformance(`executeMCPTool_${name}`, startTime);
        throw new Error(result.error);
      }

      this.recordPerformance(`executeMCPTool_${name}`, startTime);
      return result.result || result.response || result;
    } catch (e: any) {
      console.error(`MCP Tool execution failed for ${name}:`, e);
      this.recordPerformance(`executeMCPTool_${name}`, startTime);
      throw new Error(`MCP Error: ${e.message}`);
    }
  }

  private validateToolArguments(args: any): any {
      // Ensure args is a valid object
      if (args === null || args === undefined) {
          // V4 FIX: For run_browser_agent, return a default task object instead of empty
          return { task: "Przejdź na stronę internetową i wykonaj zadanie użytkownika" };
      }

    if (typeof args !== 'object') {
      return { value: args };
    }

    // Remove undefined values and sanitize
    const cleanedArgs: Record<string, any> = {};
    for (const [key, value] of Object.entries(args)) {
      if (value !== undefined && value !== null && value !== '') {
        // Sanitize string values
        if (typeof value === 'string') {
          // NOTE: do NOT strip < > here — it breaks HTML/XML content in tool arguments
          cleanedArgs[key] = value;
        } else {
          cleanedArgs[key] = value;
        }
      }
    }

    return cleanedArgs;
  }

  // Enhanced MCP audit and monitoring methods
  async getMCPServerHealth(): Promise<any> {
    const startTime = Date.now();
    try {
      const response = await fetch('/v1/mcp/health', { headers: authHeaders() });
      if (!response.ok) {
        this.recordPerformance('getMCPServerHealth', startTime);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      this.recordPerformance('getMCPServerHealth', startTime);
      return data;
    } catch (error) {
      this.recordPerformance('getMCPServerHealth', startTime);
      console.error('MCP Health check failed:', error);
      return {
        status: 'unreachable',
        servers: {},
        total_servers: 0,
        timestamp: new Date().toISOString(),
        error: (error as Error).message
      };
    }
  }

  async getMCPAuditLogs(filters: { count?: number; toolName?: string; success?: boolean; userId?: string } = {}): Promise<any> {
    const startTime = Date.now();
    try {
      const queryParams = new URLSearchParams();
      if (filters.count) queryParams.append('count', filters.count.toString());
      if (filters.toolName) queryParams.append('tool_name', filters.toolName);
      if (filters.success !== undefined) queryParams.append('success', filters.success.toString());
      if (filters.userId) queryParams.append('user_id', filters.userId);

      const response = await fetch(`/v1/mcp/audit/logs?${queryParams.toString()}`, { headers: authHeaders() });
      if (!response.ok) {
        this.recordPerformance('getMCPAuditLogs', startTime);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      this.recordPerformance('getMCPAuditLogs', startTime);
      return data;
    } catch (error) {
      this.recordPerformance('getMCPAuditLogs', startTime);
      console.error('MCP Audit logs retrieval failed:', error);
      return { logs: [], total: 0, error: (error as Error).message };
    }
  }

  async getMCPStatistics(): Promise<any> {
    const startTime = Date.now();
    try {
      const response = await fetch('/v1/mcp/audit/statistics', { headers: authHeaders() });
      if (!response.ok) {
        this.recordPerformance('getMCPStatistics', startTime);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      this.recordPerformance('getMCPStatistics', startTime);
      return data;
    } catch (error) {
      this.recordPerformance('getMCPStatistics', startTime);
      console.error('MCP Statistics retrieval failed:', error);
      return {
        statistics: {
          total_executions: 0,
          success_rate: 0,
          avg_execution_time: 0,
          failures: 0
        },
        error: (error as Error).message
      };
    }
  }

  async transcribeAudio(audioBlob: Blob, overrideModel?: string): Promise<string> {
    const startTime = Date.now();
    const { asr } = this.config;
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.wav');
    formData.append('model', overrideModel || asr.modelId || 'faster-whisper-api');

    // Create a simplified cache key based on audio blob properties
    const cacheKey = `transcribe_${audioBlob.size}_${audioBlob.type}`;
    const cachedResponse = this.getCachedResponse(cacheKey);
    if (cachedResponse) {
      this.recordPerformance('transcribeAudio_cached', startTime);
      return cachedResponse;
    }

    try {
      const response = await fetch('/v1/audio/transcriptions', {
        method: 'POST',
        headers: { ...authHeaders() },
        body: formData
      });
      const data = await this.safeJsonParse(response);
      const result = data.text || "";

      // Cache the response
      this.setCachedResponse(cacheKey, result);
      this.recordPerformance('transcribeAudio', startTime);
      return result;
    } catch (e: any) {
      const errorResult = "ASR Error: " + e.message;
      this.setCachedResponse(cacheKey, errorResult);
      this.recordPerformance('transcribeAudio_error', startTime);
      return errorResult;
    }
  }

  async synthesizeSpeech(text: string, language: string = 'en', overrideModel?: string, speed: number = 1.0): Promise<ArrayBuffer | null> {
    const { tts } = this.config;
    try {
      const ttsVoice = tts.voiceId || (language === 'pl' ? 'goscia_clean' : 'alloy');
      const requestBody = {
        model: overrideModel || tts.modelId || 'tts-1',
        input: text,
        voice: ttsVoice,
        language: language,
        stream: false,
        speed: speed
      };

      // Generate cache key
      const cacheKey = this.generateCacheKey('/v1/audio/speech', requestBody);
      const cachedResponse = this.getCachedResponse(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      const response = await fetch('/v1/audio/speech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error("TTS Error");
      const audioData = await response.arrayBuffer();

      // Cache the response
      this.setCachedResponse(cacheKey, audioData);
      return audioData;
    } catch {
      return null;
    }
  }

  private async findLocalModel(category: string): Promise<ProviderConfig | null> {
    const local = localStorage.getItem('omnigate_models');
    if (!local) return null;
    const models: ModelConfig[] = JSON.parse(local);
    const found = models.find(m => m.category === category && m.status === 'ONLINE');
    if (!found) return null;
    return { type: 'LOCAL', baseUrl: found.endpoint, modelId: found.name };
  }

  async getRepairSuggestion(errorLog: string): Promise<string> {
    try {
      // Check cache first
      const cacheKey = `repair_${btoa(errorLog.slice(0, 100))}`;
      const cachedResponse = this.getCachedResponse(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      // Make API call to backend diagnostic service
      const response = await fetch('/api/v1/diagnostic/repair-suggestion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          error_log: errorLog,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Diagnostic service error: ${response.status}`);
      }

      const data = await response.json();
      const suggestion = data.suggestion || "No repair suggestion available.";

      // Cache the response
      this.setCachedResponse(cacheKey, suggestion);
      return suggestion;
    } catch (error: any) {
      console.error('Failed to get repair suggestion:', error);
      // Fallback to local analysis
      return this.analyzeErrorLocally(errorLog);
    }
  }

  async applyRepairPatch(suggestion: string, errorLog: string): Promise<{
    success: boolean;
    message: string;
    appliedActions: string[];
    skippedActions: string[];
  }> {
    try {
      const response = await fetch('/api/v1/diagnostic/apply-patch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          suggestion,
          error_log: errorLog
        })
      });

      if (!response.ok) {
        throw new Error(`Apply patch failed: ${response.status}`);
      }

      const data = await response.json();
      return {
        success: Boolean(data.success),
        message: data.message || 'Patch operation completed.',
        appliedActions: Array.isArray(data.applied_actions) ? data.applied_actions : [],
        skippedActions: Array.isArray(data.skipped_actions) ? data.skipped_actions : []
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Patch apply failed.',
        appliedActions: [],
        skippedActions: []
      };
    }
  }

  private analyzeErrorLocally(errorLog: string): string {
    const lowerLog = errorLog.toLowerCase();

    // Neural link fault patterns
    if (lowerLog.includes('network') || lowerLog.includes('connection')) {
      return `[NEURAL LINK FAULT DETECTED]

DIAGNOSIS: Network connectivity issue detected in neural pathways.

REPAIR PROTOCOL:
1. Verify network interfaces are active
2. Check firewall rules for blocked connections
3. Restart network services
4. Validate DNS resolution
5. Test direct connectivity to core services

CONFIDENCE: HIGH (95%)`;
    }

    // Service unavailability patterns
    if (lowerLog.includes('service') && (lowerLog.includes('unavailable') || lowerLog.includes('not found'))) {
      return `[SERVICE AVAILABILITY FAULT]

DIAGNOSIS: Critical service component offline or unreachable.

REPAIR PROTOCOL:
1. Identify affected service from error context
2. Check service status via systemctl or process list
3. Review service logs for crash patterns
4. Restart affected service
5. Monitor service health post-restart

CONFIDENCE: HIGH (90%)`;
    }

    // Resource exhaustion patterns
    if (lowerLog.includes('memory') || lowerLog.includes('disk') || lowerLog.includes('resource')) {
      return `[RESOURCE EXHAUSTION DETECTED]

DIAGNOSIS: System resource depletion affecting neural operations.

REPAIR PROTOCOL:
1. Monitor current resource utilization
2. Identify resource-intensive processes
3. Terminate non-critical high-resource processes
4. Clear temporary files and caches
5. Scale resources or redistribute load

CONFIDENCE: MEDIUM (75%)`;
    }

    // Authentication/security patterns
    if (lowerLog.includes('auth') || lowerLog.includes('permission') || lowerLog.includes('access denied')) {
      return `[SECURITY/AUTHENTICATION FAULT]

DIAGNOSIS: Access control violation or credential failure.

REPAIR PROTOCOL:
1. Verify authentication tokens and credentials
2. Check user permissions and role assignments
3. Review security policies and firewall rules
4. Rotate compromised credentials if detected
5. Audit access logs for unauthorized attempts

CONFIDENCE: HIGH (85%)`;
    }

    // Generic fallback for unknown errors
    return `[AUTONOMOUS DIAGNOSTIC ANALYSIS]

DIAGNOSIS: Unrecognized error pattern requiring adaptive analysis.

REPAIR PROTOCOL:
1. Isolate error source through systematic testing
2. Check system logs for correlated events
3. Validate configuration files for syntax errors
4. Perform incremental system restart
5. Engage advanced diagnostic protocols

CONFIDENCE: LOW (60%)

RECOMMENDATION: Escalate to human operator for manual intervention.`;
  }
}

// Relatywny URL - działa zarówno lokalnie jak i przez Telebit (bez CORS blokady)
const _origin = typeof window !== 'undefined' ? window.location.origin : '';
export const aiService = new AIService({
  activeProvider: 'CUSTOM',
  vision: { type: 'CUSTOM', modelId: 'qwen2.5vl:3b', baseUrl: _origin },
  asr: { type: 'CUSTOM', modelId: 'faster-whisper-api', baseUrl: _origin },
  tts: { type: 'CUSTOM', modelId: 'tts-1', voiceId: 'goscia_clean', baseUrl: _origin },
  agent: { type: 'CUSTOM', modelId: 'steellord81/deepseek-r1:latest', baseUrl: _origin }
});